export const ORDER_TYPE_IDS = {
  NORMAL: "1",
  UNPAID: "2",
  EVENT: "3",
  STORAGE: "4",
  PAID_SAMPLE: "5",
  HYBRID: "6",
  POD: "7",
} as const;

export type OrderTypeId = (typeof ORDER_TYPE_IDS)[keyof typeof ORDER_TYPE_IDS];

export type ProjectRequestFieldKey =
  | "company_name"
  | "brand_name"
  | "client_poc_name"
  | "client_poc_number"
  | "mockup_status"
  | "expected_delivery_date"
  | "is_delivery_time_flexible"
  | "diy_redemption"
  | "mode_of_shipment"
  | "type_of_shipping"
  | "payment_status"
  | "payment_term"
  | "dispatch_date"
  | "amount"
  | "purchase_order"
  | "order_type"
  | "warehouse"
  | "order_notes";

type OrderTypeFieldConfig = {
  show: Record<ProjectRequestFieldKey, boolean>;
  required: Record<ProjectRequestFieldKey, boolean>;
};

/**
 * Define all fields once (no runtime Object.keys)
 */
const ALL_FIELDS: ProjectRequestFieldKey[] = [
  "company_name",
  "brand_name",
  "client_poc_name",
  "client_poc_number",
  "mockup_status",
  "expected_delivery_date",
  "is_delivery_time_flexible",
  "mode_of_shipment",
  "type_of_shipping",
  "payment_status",
  "payment_term",
  "dispatch_date",
  "amount",
  "purchase_order",
  "order_type",
  "warehouse",
  "order_notes",
];

/**
 * Single-pass config generator (optimized)
 */
const createFieldConfig = (
  hiddenFields: ProjectRequestFieldKey[] = [],
): OrderTypeFieldConfig => {
  const hiddenSet = new Set(hiddenFields);

  const show = {} as Record<ProjectRequestFieldKey, boolean>;
  const required = {} as Record<ProjectRequestFieldKey, boolean>;

  for (const field of ALL_FIELDS) {
    const isVisible = !hiddenSet.has(field);
    show[field] = isVisible;
    required[field] = isVisible && field !== "payment_term";
  }

  return { show, required };
};

/**
 * Mapping (fully type-safe)
 */
export const orderTypeFieldsMapping: Record<OrderTypeId, OrderTypeFieldConfig> =
  {
    [ORDER_TYPE_IDS.NORMAL]: createFieldConfig(),

    [ORDER_TYPE_IDS.UNPAID]: createFieldConfig([
      "mockup_status",
      "expected_delivery_date",
      "is_delivery_time_flexible",
      "mode_of_shipment",
      "type_of_shipping",
      "payment_status",
      "payment_term",
      "dispatch_date",
      "warehouse",
    ]),

    [ORDER_TYPE_IDS.EVENT]: createFieldConfig(),

    [ORDER_TYPE_IDS.STORAGE]: createFieldConfig(),

    [ORDER_TYPE_IDS.PAID_SAMPLE]: createFieldConfig([
      "payment_status",
      "payment_term",
    ]),

    [ORDER_TYPE_IDS.HYBRID]: createFieldConfig(),

    [ORDER_TYPE_IDS.POD]: createFieldConfig([
      "payment_status",
      "payment_term",
      "type_of_shipping",
      "mode_of_shipment",
      "dispatch_date",
    ]),
  };

/**
 * Label → ID mapping
 */
const ORDER_TYPE_LABEL_TO_ID: Record<string, OrderTypeId> = {
  normal: ORDER_TYPE_IDS.NORMAL,
  unpaid: ORDER_TYPE_IDS.UNPAID,
  event: ORDER_TYPE_IDS.EVENT,
  storage: ORDER_TYPE_IDS.STORAGE,
  "paid sample": ORDER_TYPE_IDS.PAID_SAMPLE,
  paidsample: ORDER_TYPE_IDS.PAID_SAMPLE,
  hybrid: ORDER_TYPE_IDS.HYBRID,
  pod: ORDER_TYPE_IDS.POD,
};

/**
 * Resolve order type safely
 */
const resolveOrderTypeId = (orderTypeValue?: string): OrderTypeId => {
  if (!orderTypeValue) return ORDER_TYPE_IDS.NORMAL;

  const normalized = String(orderTypeValue).trim();

  if (normalized in orderTypeFieldsMapping) {
    return normalized as OrderTypeId;
  }

  return (
    ORDER_TYPE_LABEL_TO_ID[normalized.toLowerCase()] || ORDER_TYPE_IDS.NORMAL
  );
};

/**
 * Final public function
 */
export const getOrderTypeFieldConfig = (
  orderTypeId?: string,
): OrderTypeFieldConfig => {
  const resolvedOrderTypeId = resolveOrderTypeId(orderTypeId);
  return orderTypeFieldsMapping[resolvedOrderTypeId];
};

/** Used when stock check is not required — company/contact, payment, and order notes. */
export const STOCK_CHECK_NOT_REQUIRED_FIELD_CONFIG = createFieldConfig([
  "mockup_status",
  "expected_delivery_date",
  "is_delivery_time_flexible",
  "mode_of_shipment",
  "type_of_shipping",
  "dispatch_date",
  "order_type",
  "warehouse",
]);

/**
 * Payment field visibility by status label.
 * Used by raise form and view popup so the rules stay in one place.
 */
const PURCHASE_ORDER_PAYMENT_STATUS_LABEL = "purchase order";

const normalizePaymentStatusLabel = (paymentStatusLabel?: string) =>
  paymentStatusLabel?.trim().toLowerCase() ?? "";

export const getPaymentFieldVisibilityFromLabel = (
  paymentStatusLabel?: string,
) => {
  const label = normalizePaymentStatusLabel(paymentStatusLabel);
  const isPaid = label === "paid";
  const isPartial = label === "partial";
  const isPurchaseOrder = label === PURCHASE_ORDER_PAYMENT_STATUS_LABEL;

  return {
    showAmount: isPaid || isPartial,
    showPurchaseOrder: isPurchaseOrder,
    showPaymentTerm: !isPaid,
  };
};
